import React, { useRef, useState, useEffect, useCallback } from 'react';
import useStyles from './styles';

import {
  FORMATOS,
  INITIAL_FORMATOS_ACTIVOS,
  INITIAL_CONFIG_TABLA,
  INITIAL_ESTADISTICAS,
} from './constantes';

import {
  alternarNegritaChip,
  normalizarChipsAlCargar,
  crearElementoChip,
  manejarNegritaEnChips,
} from './utilidades/chipHelper';

import {
  obtenerContextoTabla,
  cambiarEstiloTabla as cambiarEstiloTablaHelper,
  insertarFila as insertarFilaHelper,
  eliminarFila as eliminarFilaHelper,
  insertarColumna as insertarColumnaHelper,
  eliminarColumna as eliminarColumnaHelper,
  eliminarTabla as eliminarTablaHelper,
  generarHtmlTabla,
} from './utilidades/tablaHelper';

import { procesarPegado } from './utilidades/pasteHelper';
import { sanitizeContractHtml } from './utilidades/sanitizer';

import BarraHerramientas from './componentes/BarraHerramientas';
import BarraEstado from './componentes/BarraEstado';

const EditorContrato = ({
  value,
  onChange,
  placeholder = 'Comience a redactar el contrato aquí...',
  onDescargarVistaPrevia,
  descargandoVistaPrevia = false,
}) => {
  const classes = useStyles();
  const editorRef = useRef(null);
  const savedSelectionRef = useRef(null);
  const activeTableContextRef = useRef({ tabla: null, fila: null, celda: null });

  const [formatoActual, setFormatoActual] = useState('p');
  const [formatosActivos, setFormatosActivos] = useState(INITIAL_FORMATOS_ACTIVOS);

  // Estados para tablas
  const [anchorTabla, setAnchorTabla] = useState(null);
  const [estaEnTabla, setEstaEnTabla] = useState(false);
  const [estiloTablaActual, setEstiloTablaActual] = useState(null);
  const [modalTablaOpen, setModalTablaOpen] = useState(false);
  const [configTabla, setConfigTabla] = useState(INITIAL_CONFIG_TABLA);

  // Estadísticas del documento
  const [estadisticas, setEstadisticas] = useState(INITIAL_ESTADISTICAS);

  // Guardar y restaurar selecciones
  const guardarSeleccion = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restaurarSeleccion = () => {
    const sel = window.getSelection();
    if (sel && savedSelectionRef.current) {
      sel.removeAllRanges();
      sel.addRange(savedSelectionRef.current);
    }
  };

  // Calcular estadísticas (palabras, caracteres, páginas aproximadas)
  const calcularEstadisticas = useCallback(() => {
    if (!editorRef.current) return;
    const texto = editorRef.current.innerText || '';
    const palabras = texto.trim() ? texto.trim().split(/\s+/).length : 0;
    const caracteres = texto.length;
    const alturaTotal = editorRef.current.scrollHeight || 1056;
    const paginasPorAltura = Math.max(1, Math.ceil(alturaTotal / 1056));
    const saltosManuales = editorRef.current.querySelectorAll('.page-break').length;
    const paginas = Math.max(saltosManuales + 1, paginasPorAltura);

    setEstadisticas({ palabras, caracteres, paginas });
  }, []);

  // Notificar cambios al componente padre
  const notificarCambio = () => {
    guardarSeleccion();
    if (editorRef.current && onChange) {
      onChange(editorRef.current.innerHTML);
    }
    actualizarFormatosActivos();
    calcularEstadisticas();
  };

  // Detectar y actualizar el estado de los formatos activos en la posición del cursor
  const actualizarFormatosActivos = useCallback(() => {
    if (!editorRef.current) return;
    const sel = window.getSelection();
    if (!sel || !sel.anchorNode || !editorRef.current.contains(sel.anchorNode)) {
      setEstaEnTabla(false);
      setEstiloTablaActual(null);
      return;
    }

    try {
      let boldActivo = document.queryCommandState('bold');

      // Detectar si el cursor está sobre un chip de variable con negrita
      if (sel.rangeCount > 0) {
        const nodo = sel.anchorNode;
        let chip = null;
        if (nodo) {
          if (nodo.nodeType === Node.ELEMENT_NODE && nodo.classList?.contains('variable-chip')) {
            chip = nodo;
          } else if (nodo.parentElement?.classList?.contains('variable-chip')) {
            chip = nodo.parentElement;
          }
        }
        if (chip && (chip.getAttribute('data-negrita') === 'true' || chip.classList.contains('chip-bold'))) {
          boldActivo = true;
        }
      }

      setFormatosActivos({
        bold: boldActivo,
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
        strikeThrough: document.queryCommandState('strikeThrough'),
        justifyLeft: document.queryCommandState('justifyLeft'),
        justifyCenter: document.queryCommandState('justifyCenter'),
        justifyRight: document.queryCommandState('justifyRight'),
        justifyFull: document.queryCommandState('justifyFull'),
        insertUnorderedList: document.queryCommandState('insertUnorderedList'),
        insertOrderedList: document.queryCommandState('insertOrderedList'),
      });

      const formatoBloque = document.queryCommandValue('formatBlock');
      if (formatoBloque) {
        const tag = formatoBloque.toLowerCase().replace(/[<>]/g, '');
        if (FORMATOS.some((f) => f.valor === tag)) {
          setFormatoActual(tag);
        }
      }

      // Detectar contexto de tabla
      const contexto = obtenerContextoTabla(editorRef.current, savedSelectionRef, activeTableContextRef);
      if (contexto.tabla) {
        setEstaEnTabla(true);
        if (contexto.tabla.classList.contains('tabla-sin-bordes')) {
          setEstiloTablaActual('sin-bordes');
        } else if (contexto.tabla.classList.contains('tabla-lineas-horizontales')) {
          setEstiloTablaActual('horizontales');
        } else {
          setEstiloTablaActual('con-bordes');
        }
      } else {
        setEstaEnTabla(false);
        setEstiloTablaActual(null);
      }
    } catch (e) {}
  }, []);

  // Escuchar cambios de selección global en el editor
  useEffect(() => {
    const handleSelectionChange = () => {
      actualizarFormatosActivos();
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [actualizarFormatosActivos]);

  // Separador de párrafo por defecto
  useEffect(() => {
    try {
      document.execCommand('defaultParagraphSeparator', false, 'p');
    } catch (e) {}
  }, []);

  // Sincronizar valor inicial y normalizar chips
  useEffect(() => {
    if (editorRef.current) {
      if (value !== undefined && value !== null && value !== '') {
        const sanitizedValue = sanitizeContractHtml(value);
        if (editorRef.current.innerHTML !== sanitizedValue) {
          editorRef.current.innerHTML = sanitizedValue;
          normalizarChipsAlCargar(editorRef.current);
          calcularEstadisticas();
        }
      } else if (!editorRef.current.innerHTML || editorRef.current.innerHTML === '<br>') {
        editorRef.current.innerHTML = '<p><br></p>';
        calcularEstadisticas();
      }
    }
  }, [value, calcularEstadisticas]);

  // Ejecución de comandos de formato de texto
  const ejecutarComando = (comando, valor = null) => {
    restaurarSeleccion();
    const sel = window.getSelection();

    if (comando === 'bold') {
      manejarNegritaEnChips(sel, editorRef.current);
    }

    document.execCommand(comando, false, valor);
    guardarSeleccion();
    actualizarFormatosActivos();
    if (editorRef.current && onChange) {
      onChange(editorRef.current.innerHTML);
    }
    calcularEstadisticas();
  };

  const cambiarFormato = (e) => {
    const tag = e.target.value;
    setFormatoActual(tag);
    ejecutarComando('formatBlock', `<${tag}>`);
  };

  // Insertar salto de página
  const insertarSaltoPagina = () => {
    restaurarSeleccion();
    const tagHtml =
      '<div class="page-break" contenteditable="false" style="page-break-after: always;"><span class="page-break-label">Salto de página</span></div><p><br></p>';
    if (!savedSelectionRef.current && editorRef.current) {
      editorRef.current.focus();
    }
    document.execCommand('insertHTML', false, tagHtml);
    if (editorRef.current && onChange) {
      onChange(editorRef.current.innerHTML);
    }
    calcularEstadisticas();
  };

  // Inserción de variable dinámica seleccionada
  const insertarVariable = (clave) => {
    restaurarSeleccion();
    const sel = window.getSelection();
    let range = null;
    if (sel && sel.rangeCount > 0) {
      range = sel.getRangeAt(0);
    }

    if (!range && editorRef.current) {
      range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
    }

    if (range && editorRef.current) {
      range.deleteContents();

      const estaEnNegrita = Boolean(formatosActivos.bold || document.queryCommandState('bold'));
      const elementoAInsertar = crearElementoChip(clave, estaEnNegrita);
      const espacioDespues = document.createTextNode('\u00A0');

      const fragment = document.createDocumentFragment();
      fragment.appendChild(elementoAInsertar);
      fragment.appendChild(espacioDespues);

      range.insertNode(fragment);

      const nuevoRange = document.createRange();
      nuevoRange.setStart(espacioDespues, 1);
      nuevoRange.collapse(true);

      if (sel) {
        sel.removeAllRanges();
        sel.addRange(nuevoRange);
      }
      savedSelectionRef.current = nuevoRange.cloneRange();
      editorRef.current.focus();

      if (onChange) {
        onChange(editorRef.current.innerHTML);
      }
    }

    calcularEstadisticas();

    setTimeout(() => {
      if (editorRef.current && savedSelectionRef.current) {
        editorRef.current.focus();
        const s = window.getSelection();
        if (s) {
          s.removeAllRanges();
          s.addRange(savedSelectionRef.current);
        }
      }
    }, 10);
  };

  // Métodos de tabla
  const cambiarEstiloTabla = (estilo) => {
    restaurarSeleccion();
    const contexto = obtenerContextoTabla(editorRef.current, savedSelectionRef, activeTableContextRef);
    cambiarEstiloTablaHelper(estilo, contexto, setEstiloTablaActual, notificarCambio);
    setAnchorTabla(null);
  };

  const insertarFila = (posicion = 'abajo') => {
    restaurarSeleccion();
    const contexto = obtenerContextoTabla(editorRef.current, savedSelectionRef, activeTableContextRef);
    insertarFilaHelper(posicion, contexto, activeTableContextRef, editorRef.current, savedSelectionRef, notificarCambio);
    setAnchorTabla(null);
  };

  const eliminarFila = () => {
    restaurarSeleccion();
    const contexto = obtenerContextoTabla(editorRef.current, savedSelectionRef, activeTableContextRef);
    eliminarFilaHelper(
      contexto,
      activeTableContextRef,
      editorRef.current,
      savedSelectionRef,
      setEstaEnTabla,
      setEstiloTablaActual,
      notificarCambio
    );
    setAnchorTabla(null);
  };

  const insertarColumna = (posicion = 'derecha') => {
    restaurarSeleccion();
    const contexto = obtenerContextoTabla(editorRef.current, savedSelectionRef, activeTableContextRef);
    insertarColumnaHelper(posicion, contexto, activeTableContextRef, editorRef.current, savedSelectionRef, notificarCambio);
    setAnchorTabla(null);
  };

  const eliminarColumna = () => {
    restaurarSeleccion();
    const contexto = obtenerContextoTabla(editorRef.current, savedSelectionRef, activeTableContextRef);
    eliminarColumnaHelper(
      contexto,
      activeTableContextRef,
      editorRef.current,
      savedSelectionRef,
      setEstaEnTabla,
      setEstiloTablaActual,
      notificarCambio
    );
    setAnchorTabla(null);
  };

  const eliminarTabla = () => {
    restaurarSeleccion();
    const contexto = obtenerContextoTabla(editorRef.current, savedSelectionRef, activeTableContextRef);
    eliminarTablaHelper(contexto, activeTableContextRef, setEstaEnTabla, setEstiloTablaActual, notificarCambio);
    setAnchorTabla(null);
  };

  const insertarNuevaTabla = (config) => {
    restaurarSeleccion();
    if (!savedSelectionRef.current && editorRef.current) {
      editorRef.current.focus();
    }
    const html = generarHtmlTabla(config);
    document.execCommand('insertHTML', false, html);
    setAnchorTabla(null);
    setModalTablaOpen(false);
    notificarCambio();
  };

  // Manejar clics directos sobre el editor (ej. alternar negrita en variables al hacer clic)
  const handleClickEditor = (e) => {
    const chip = e.target.closest('.variable-chip');
    if (chip) {
      e.preventDefault();
      e.stopPropagation();
      alternarNegritaChip(chip, savedSelectionRef, notificarCambio);
      return;
    }
    guardarSeleccion();
    actualizarFormatosActivos();
  };

  // Atajos de teclado
  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      insertarSaltoPagina();
      return;
    }
    if ((e.ctrlKey || e.metaKey) && (e.key === 'b' || e.key === 'B')) {
      e.preventDefault();
      ejecutarComando('bold');
      return;
    }
  };

  // Manejar pegado de contenido
  const handlePaste = (e) => {
    procesarPegado(e, editorRef, notificarCambio);
  };

  const handleInput = useCallback(() => {
    guardarSeleccion();
    if (editorRef.current && onChange) {
      onChange(editorRef.current.innerHTML);
    }
    calcularEstadisticas();
  }, [onChange, calcularEstadisticas]);

  return (
    <div className={classes.root}>
      {/* Barra de herramientas completa */}
      <BarraHerramientas
        classes={classes}
        formatoActual={formatoActual}
        onCambiarFormato={cambiarFormato}
        formatosActivos={formatosActivos}
        onEjecutarComando={ejecutarComando}
        onInsertarSaltoPagina={insertarSaltoPagina}
        // Tablas
        estaEnTabla={estaEnTabla}
        estiloTablaActual={estiloTablaActual}
        anchorTabla={anchorTabla}
        onAbrirMenuTabla={(e) => {
          guardarSeleccion();
          setAnchorTabla(e.currentTarget);
        }}
        onCerrarMenuTabla={() => setAnchorTabla(null)}
        onCambiarEstiloTabla={cambiarEstiloTabla}
        onInsertarFila={insertarFila}
        onEliminarFila={eliminarFila}
        onInsertarColumna={insertarColumna}
        onEliminarColumna={eliminarColumna}
        onEliminarTabla={eliminarTabla}
        onInsertarNuevaTabla={insertarNuevaTabla}
        modalTablaOpen={modalTablaOpen}
        onAbrirModalPersonalizar={() => {
          setAnchorTabla(null);
          setModalTablaOpen(true);
        }}
        onCerrarModalPersonalizar={() => setModalTablaOpen(false)}
        configTabla={configTabla}
        setConfigTabla={setConfigTabla}
        // Variables
        onInsertarVariable={insertarVariable}
        onGuardarSeleccion={guardarSeleccion}
        // Vista previa
        onDescargarVistaPrevia={onDescargarVistaPrevia}
        descargandoVistaPrevia={descargandoVistaPrevia}
      />

      {/* Contenedor tipo hoja de Word */}
      <div
        className={classes.documentContainer}
        onClick={() => editorRef.current && editorRef.current.focus()}
      >
        <div
          ref={editorRef}
          className={classes.sheetPage}
          contentEditable
          suppressContentEditableWarning
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onClick={handleClickEditor}
          onInput={() => {
            handleInput();
            actualizarFormatosActivos();
          }}
          onKeyUp={() => {
            guardarSeleccion();
            actualizarFormatosActivos();
          }}
          onMouseUp={() => {
            guardarSeleccion();
            actualizarFormatosActivos();
          }}
          onBlur={guardarSeleccion}
          data-placeholder={placeholder}
        />
      </div>

      {/* Barra de estado con estadísticas */}
      <BarraEstado classes={classes} estadisticas={estadisticas} />
    </div>
  );
};

export default React.memo(EditorContrato);
